import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'cobstream:subtitleLang';

/**
 * The user's persisted subtitle language preference. `'off'` means subtitles
 * are explicitly disabled; any other string is a two-letter language code
 * (e.g. `'en'`, `'es'`). The picker writes whatever language the user clicks,
 * including languages that come from OpenSubtitles, so callers must not
 * assume the value matches a currently-available track.
 */
export type SubtitlePref = string | 'off';

function readPref(): SubtitlePref {
  if (typeof window === 'undefined') return 'off';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (typeof raw === 'string' && raw.length > 0) return raw;
  } catch {
    // Storage access can throw in private-mode/SSR-ish edge cases; fall through.
  }
  return 'off';
}

function writePref(value: SubtitlePref): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Same edge cases as above — silently no-op so the UI stays responsive.
  }
}

export interface UseSubtitlePref {
  lang: SubtitlePref;
  setLang: (next: SubtitlePref) => void;
}

export function useSubtitlePref(): UseSubtitlePref {
  const [lang, setLangState] = useState<SubtitlePref>(() => readPref());

  // Sync if another tab updates the pref so a user toggling subtitles in
  // one window doesn't see stale state in another.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function onStorage(e: StorageEvent): void {
      if (e.key === STORAGE_KEY) setLangState(readPref());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setLang = useCallback((next: SubtitlePref): void => {
    writePref(next);
    setLangState(next);
  }, []);

  return { lang, setLang };
}
